/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ProfileTab } from "enums/ProfileTabs";

import { useWorkItemsFromSOW } from "services/input-core/work-items/get-work-items-from-sow";
import { useSubcontractor } from "services/input-core/subcontractors/get-subcontractor";
import { useWorkersBySubcontractor } from "services/input-core/subcontractors/get-workers-by-subcontractor";
import { useGetSOWsByIdSowOperativeQuery } from "services/input-core/sows/get-sow-operative-by-id";

import { ArrowForwardIos } from "@material-ui/icons";

import GridContainer from "components/UI/Grid/GridContainer";
import GridItemTS from "components/UI/Grid/GridItemTS";
import { TabContent, TabSystem } from "components/UI/TabSystem";
import Header from "./components/Header";
import SOWsRelated from "./components/SOWsRelated/SOWsRelated";
import { WorkerRelatedTable } from "components/Tables/WorkerRelatedTable";
import ButtonTS from "components/Forms/CustomButtons/ButtonTS";

import GeneralTab, { BuildDetailsSection } from "./tabs/GeneralTab/GeneralTab";
import ActivityItemsTab from "./tabs/ActivityItemsTab/ActivityItemsTab";

import {
  parseDateAndTime,
  tryRetrieveDateFromUrlParams,
} from "helpers/DateHelper";
import { useActivityItemsFromSOWWithStacks } from "services/input-core/activity-items/get-activity-item-from-sow-adm";
import {
  Line,
  StyledParagraph,
  StyledParagraphValue,
} from "./tabs/GeneralTab/GeneralTab.styles";
import { useActivityItemContext } from "store/activityItem";

const SubcontractorProfile: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("sow-profile");

  const [searchParams] = useSearchParams();
  const subcontractorId = searchParams.get("id");

  const { data, isLoading } = useSubcontractor(subcontractorId);
  const [date, setDate] = React.useState<Date>(() =>
    tryRetrieveDateFromUrlParams(),
  );

  const {
    setActivityItems,
    setLoading: setLoadingActivity,
    setRefetching: setRefetchingActivity,
  } = useActivityItemContext();

  const {
    data: activityItemsQuery,
    isLoading: isActivityItemsLoadingQuery,
    isRefetching: isActivityItemsRefetchingQuery,
    isInitialLoading: isActivityItemsInitialLoading,
    refetch: refetchActivityItems,
  } = useActivityItemsFromSOWWithStacks(data?.sowId, date, {
    enabled: !isLoading,
  });

  const {
    data: workItems,
    isLoading: isWorkItemsLoading,
    refetch: refetchWorkItems,
  } = useWorkItemsFromSOW(data?.sowId, date, {
    enabled: !isLoading,
  });

  const {
    data: workersFromSubcontractor,
    isLoading: isLoadingWorkersFromSubcontractor,
  } = useWorkersBySubcontractor({
    subcontractorName: data?.name,
    taxId: data?.taxId,
    supplierCode: data?.supplierCode,
  });

  const {
    data: sows,
    isLoading: isLoadingSows,
  } = useGetSOWsByIdSowOperativeQuery(data?.sowId, { enabled: !isLoading });

  useEffect(() => {
    setLoadingActivity(isActivityItemsLoadingQuery);
    setRefetchingActivity(isActivityItemsRefetchingQuery);
    if (activityItemsQuery) setActivityItems(activityItemsQuery);

  }, [activityItemsQuery, isActivityItemsLoadingQuery, isActivityItemsRefetchingQuery, setActivityItems, setLoadingActivity, setRefetchingActivity]);

  const tabs = [
    {
      id: ProfileTab.GENERAL,
      title: t("General", { ns: "profile" }),
    },
    {
      id: ProfileTab.ACTIVITY_ITEMS,
      title: t("Activity Items", { ns: "profile" }),
    },
  ];

  const sowBuildDetails: BuildDetailsSection[] = [
    {
      title: t("SOW Details", { ns: "profile" }),
      items: [
        {
          text: t("SOW ID", { ns: "profile" }),
          value: data?.sowId,
        },
        {
          text: t("SOW Name", { ns: "profile" }),
          value: data?.name,
        },
        {
          text: t("Supplier", { ns: "profile" }),
          value: data?.supplierCode,
        },
      ],
    },
    {
      title: t("Secondary Details", { ns: "profile" }),
      items: [
        {
          text: t("Billable", { ns: "profile" }),
          value: t(data?.billable),
        },
        {
          text: t("Created At", { ns: "profile" }),
          value: parseDateAndTime(data?.createdAt),
        },
        {
          text: t("Updated At", { ns: "profile" }),
          value: parseDateAndTime(data?.updatedAt),
        },
      ],
    },
    {
      title: t("Custom Fields", { ns: "profile" }),
      items: [
        {
          text: t("Fields for Uverify", { ns: "profile" }),
          value: (
            <div>
              {data?.customFields?.map(
                (
                  field: {
                    name: string;
                    value: any;
                  },
                  index: number,
                ) => (
                  <Line key={index}>
                    <StyledParagraph>
                      {t(field?.name, { ns: "custom-fields" })}:
                    </StyledParagraph>
                    {typeof field.value === "object" ? (
                      <StyledParagraphValue>
                        {JSON.stringify(field.value)}
                      </StyledParagraphValue>
                    ) : (
                      <StyledParagraphValue>
                        {t(field.value, { ns: "profile" })}
                      </StyledParagraphValue>
                    )}
                  </Line>
                ),
              )}
            </div>
          ),
        },
      ],
    },
  ];

  const workersFormatted = (data: any) =>
    data?.map(
      (worker: {
        workerID: string;
        firstName: string;
        lastName: string;
        workerEmail: string;
      }) => ({
        code: worker.workerID,
        name: `${worker.firstName + " "}${worker.lastName}`,
        email: worker.workerEmail,
        actions: (
          <div className="actions-right">
            <ButtonTS
              justIcon
              round
              color="info"
              size="sm"
              onClick={() =>
                navigate(
                  `/app/workers/profile?profile=worker"&id=${worker.workerID}`,
                )
              }
            >
              <ArrowForwardIos />
            </ButtonTS>
          </div>
        ),
      }),
    ) || [];

  return (
    <GridContainer>
      <GridItemTS xs={12}>
        <Header
          data={{
            fieldglassId: data?.sowId,
            name: data?.name,
            status: data?.status,
            zIdentifier: data?.zId,
            startDate: data?.sow?.startDate,
            endDate: data?.sow?.endDate,
          }}
          isLoading={isLoading}
          type="sow"
        />
      </GridItemTS>

      <GridItemTS xs={12} style={{ marginTop: "1rem" }}>
        <TabSystem tabs={tabs} syncWithUrlParams>
          <TabContent value={ProfileTab.GENERAL}>
            <GeneralTab isLoading={isLoading} buildDetails={sowBuildDetails} />
          </TabContent>

          <TabContent value={ProfileTab.ACTIVITY_ITEMS}>
            <ActivityItemsTab
              fieldglassId={data?.sowId}
              entityDate={{ start: data?.startDate, end: data?.endDate }}
              date={date}
              setDate={setDate}
              isActivityItemsInitialLoading={isActivityItemsInitialLoading}
              refetchActivityItems={refetchActivityItems}
              workItems={workItems}
              isWorkItemsLoading={isWorkItemsLoading}
              refetchWorkItems={refetchWorkItems}
              profile={"SOW"}
              supplierCode={data?.supplierCode}
            />
          </TabContent>

          <TabContent value={ProfileTab.SOWS_RELATED}>
            <SOWsRelated
              sows={sows?.sowAdms}
              isLoading={isLoadingSows}
              isSowAdm={false}
            />
          </TabContent>
          <TabContent value={ProfileTab.WORKERS_RELATED}>
            <WorkerRelatedTable
              data={
                workersFromSubcontractor
                  ? workersFormatted(workersFromSubcontractor)
                  : []
              }
              isLoading={isLoadingWorkersFromSubcontractor}
              omit={["email", "vendorName", "vendorCode", "numberDNI"]}
              initialRowsPerPage={5}
            />
          </TabContent>
        </TabSystem>
      </GridItemTS>
    </GridContainer>
  );
};

export default SubcontractorProfile;
